import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  InputAdornment, IconButton, Alert, CircularProgress
} from '@mui/material';
import { Email, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await InvokeApi(Config.main_url + apiList.adminLogin, { email, password });
      if (data?.code === '200') {
        await login({ token: data.token, role: data.role, adminId: data.adminId });
        navigate(data.role === 'Tracking' ? '/vehicles' : '/dashboard' );
      } else {
        setError(data?.message || 'Invalid credentials');
      }
    } catch {
      setError('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #1565c0 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden',
    }}>
      {[
        { w: 350, h: 350, top: '-10%', left: '-8%' },
        { w: 200, h: 200, top: '65%', left: '80%' },
        { w: 450, h: 450, top: '-15%', left: '65%' },
        { w: 150, h: 150, top: '75%', left: '-3%' },
      ].map((s, i) => (
        <Box key={i} sx={{
          position: 'absolute', borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          width: s.w, height: s.h, top: s.top, left: s.left,
        }} />
      ))}

      <Card sx={{
        width: '100%', maxWidth: 430, mx: 2, borderRadius: 4,
        boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
        background: 'rgba(255,255,255,0.98)',
        position: 'relative', zIndex: 1,
      }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{
              width: 90, height: 90, borderRadius: 3, mx: 'auto', mb: 2,
              background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(26,35,126,0.2)',
              p: 1.5, border: '1px solid #e8eaf6',
            }}>
              <img src={logo} alt="St. Lord's International Public School" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 6 }} />
            </Box>
            <Typography variant="h5" fontWeight={800} color="#1a237e" letterSpacing={0.5}>
              St. Lord's International Public School
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Admin Panel
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth label="Email Address" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              required sx={{ mb: 2.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#1a237e' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth label="Password"
              type={showPass ? 'text' : 'password'}
              value={password} onChange={(e) => setPassword(e.target.value)}
              required sx={{ mb: 3.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#1a237e' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPass(!showPass)} edge="end">
                      {showPass ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit" fullWidth variant="contained" size="large"
              disabled={loading}
              sx={{
                py: 1.6, borderRadius: 2, fontWeight: 700, fontSize: 16,
                background: 'linear-gradient(135deg, #1a237e, #1565c0)',
                boxShadow: '0 8px 24px rgba(26,35,126,0.4)',
                '&:hover': { background: 'linear-gradient(135deg, #0d1b6e, #0d47a1)', boxShadow: '0 12px 32px rgba(26,35,126,0.5)' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </form>

          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={3}>
            © 2026 St. Lord's International Public School · All rights reserved
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
